import { CCS_CONDITIONS, CCSCondition } from "./types";
import { getFormSections } from "./formSchema";

/** Narrows an arbitrary string (e.g. a Firestore `condition` field) to `CCSCondition`. */
export function isCCSCondition(value: unknown): value is CCSCondition {
  return typeof value === "string" && (CCS_CONDITIONS as string[]).includes(value);
}

/**
 * Resolves the human-readable label for a form field id under a given
 * condition's schema. Returns `undefined` if the condition or field id is
 * not (or no longer) part of the schema — callers should fall back to the
 * raw field id in that case.
 */
export function getFieldLabel(condition: CCSCondition, fieldId: string): string | undefined {
  for (const section of getFormSections(condition)) {
    const field = section.fields.find((f) => f.id === fieldId);
    if (field) return field.label || undefined;
  }
  return undefined;
}

export interface ConsultationDetailRow {
  fieldId: string;
  label: string;
  value: string;
}

export interface ConsultationDetailSection {
  id: string;
  title: string;
  rows: ConsultationDetailRow[];
}

const EMPTY_VALUE_PLACEHOLDER = "\u2014"; // em dash, matches pdfGenerator's fieldVal()

function displayValue(raw: unknown): string {
  return String(raw ?? "").trim() || EMPTY_VALUE_PLACEHOLDER;
}

/**
 * Groups a consultation's `formData` into ordered, human-readable sections
 * for display in the admin panels, mirroring pdfGenerator.ts's existing
 * ordering/skip rules:
 *  - Sections and fields are ordered per the condition's schema.
 *  - Signature fields and fields marked `hidden` are skipped (they are not
 *    meaningful to show as a "row" — signatures render as images and
 *    hidden fields are internal computed state).
 *  - Sections with no visible fields are dropped entirely.
 *  - Missing or blank values default to an em dash, exactly like the PDF.
 *
 * `formData` keys not covered by the condition's current schema (e.g. older
 * records saved under a schema that has since changed, or an unknown
 * condition) are simply omitted — they are not surfaced as their own
 * section.
 */
export function groupFormDataIntoSections(
  condition: CCSCondition | string,
  formData: Record<string, string> | null | undefined,
): ConsultationDetailSection[] {
  const fd = formData ?? {};
  const schemaSections = isCCSCondition(condition) ? getFormSections(condition) : [];
  const result: ConsultationDetailSection[] = [];

  for (const section of schemaSections) {
    const visibleFields = section.fields.filter((f) => f.type !== "signature" && !f.hidden);
    if (visibleFields.length === 0) continue;

    const rows: ConsultationDetailRow[] = visibleFields.map((field) => ({
      fieldId: field.id,
      label: field.label || field.id,
      value: displayValue(fd[field.id]),
    }));

    result.push({ id: section.id, title: section.title, rows });
  }

  return result;
}
